import { createContext, useContext, useState, useRef, useCallback,useEffect } from "react";

// Step 1: Create the context
// Initially null, will be filled by Provider
const AIContext = createContext(null);

// Step 2: Create a custom hook for easy access
// This is a convenience wrapper so components just call useAI()
export function useAI() {
  const context = useContext(AIContext);
  
  // Safety check: make sure we're inside a Provider
  if (!context) {
    throw new Error("useAI must be used within AIProvider");
  }
  
  return context;
}

// Step 3: Create the Provider component
export function AIProvider({ children }) {
  
  // ============================================
  // STATE DEFINITIONS
  // ============================================
  
  // Status of the AI task
  // "idle" = nothing happening
  // "running" = AI is currently processing
  // "finished" = AI completed successfully
  // "error" = something went wrong
  const [status, setStatus] = useState("idle");
  const statusRef = useRef("idle");
  
  // Current conversation history
  // Array of message objects: [{ id, type, content, timestamp, ... }, ...]
    const [conversation, setConversation] = useState(() => {
    // Try to restore conversation from localStorage on mount
    try {
        const saved = localStorage.getItem('ai_conversation_backup');
        if (saved) {
        const parsed = JSON.parse(saved);
        console.log('📂 Restored conversation from localStorage');
        return parsed;
        }
    } catch (error) {
        console.error('Failed to restore conversation:', error);
    }
    return [];
    });
  
  // The current question being asked
  const [prompt, setPrompt] = useState("");
  
  // Which AI model to use (e.g., "agent")
  const [model, setModel] = useState(null);

  // Active model selected by user (for dropdown)
const [activeModel, setActiveModel] = useState(() => {
  const saved = localStorage.getItem('ai_selected_model');
  return saved || 'auto-v1';
});
  
  // Partial response as it streams in (real-time updates)
  const [partial, setPartial] = useState("");
  
  // Complete final response after streaming finishes
  const [final, setFinal] = useState("");
  
  // Whether to show notification badge (for when user is on different page)
  const [badgeVisible, setBadgeVisible] = useState(false);
  // 'success' | 'error' - used for notification styling when message completes or fails
  const [lastResultType, setLastResultType] = useState('success');
  
  // Metadata for messages (thinking process, follow-ups, etc.)
  const [messageMetadata, setMessageMetadata] = useState({});
  // Structure: { messageId: { thinking: "...", followUps: [...], responseTime: 5 } }
  
  // ============================================
  // REFS (values that persist but don't trigger re-renders)
  // ============================================
  
  // AbortController to cancel ongoing requests
  const abortRef = useRef(null);

  // Monotonic request id so old requests cannot update a newer answer
  const runIdRef = useRef(0);
  const activeRunIdRef = useRef(null);
  
  // When the current task started (for calculating response time)
  const startTimeRef = useRef(null);
  
  // The ID of the current AI message being streamed
  const currentMessageIdRef = useRef(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

    useEffect(() => {
    // Only save if there's actual conversation data
    if (conversation.length > 0) {
        try {
        localStorage.setItem(
            'ai_conversation_backup',
            JSON.stringify(conversation)
        );
        } catch (error) {
        console.error('Failed to save conversation:', error);
        }
    }
    }, [conversation]);

    useEffect(() => {
  if (activeModel) {
    localStorage.setItem('ai_selected_model', activeModel);
  }
}, [activeModel]);
  
  // ============================================
  // ACTIONS (functions components can call)
  // ============================================
  
  /**
   * Start a new AI task
   * @param {string} question - The user's question
   * @param {string} selectedModel - Which AI model to use
   */
  const startTask = useCallback((question, selectedModel) => {
    // Prevent starting if already running
    if (statusRef.current === "running") {
      console.warn("Task already running, ignoring new request");
      return;
    }

    // Create user message
    const userMessage = {
      id: Date.now(),
      type: "user",
      content: question,
      timestamp: new Date().toISOString()
    };
    
    // Create AI message (initially empty, will be filled by streaming)
    const aiMessageId = Date.now() + 1;
    const aiMessage = {
      id: aiMessageId,
      type: "ai",
      content: "",
      timestamp: new Date().toISOString(),
      model: selectedModel,
      isThinking: true,
      isStreaming: false
    };
    
    // Add both messages to conversation
    setConversation(prev => [...prev, userMessage, aiMessage]);
    
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    activeRunIdRef.current = runId;

    // Store current message ID for BackgroundRunner to update
    currentMessageIdRef.current = aiMessageId;
    
    // Create AbortController *before* status becomes "running" so Stop button works
    // immediately during "thinking" (before BackgroundRunner's useEffect runs).
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    
    // Set task parameters
    setPrompt(question);
    setModel(selectedModel || "prometheus-agent");
    setPartial("");
    setFinal("");
    
    // Change status to "running" - this triggers BackgroundRunner
    statusRef.current = "running";
    setStatus("running");
    
    // Record start time for response time calculation
    startTimeRef.current = Date.now();
    
    console.log("Task started:", { question, model: selectedModel, messageId: aiMessageId, runId });
  }, []);
  
  /**
   * Update the partial response as it streams in
   * Called by BackgroundRunner with each chunk
   */
  const updatePartial = useCallback((content, runId = activeRunIdRef.current, messageId = currentMessageIdRef.current) => {
    if (!runId || runId !== activeRunIdRef.current) {
      return false;
    }

    setPartial(content);
    
    // Update the AI message in conversation with streaming content
    setConversation(prev => prev.map(msg =>
      msg.id === messageId
        ? {
            ...msg,
            content: content,
            isThinking: false,
            isStreaming: true
          }
        : msg
    ));
    return true;
  }, []);
  
  /**
   * Mark task as finished
   * Called by BackgroundRunner when streaming completes
   */
  const setFinished = useCallback((finalContent, runId = activeRunIdRef.current, messageId = currentMessageIdRef.current) => {
    if (!runId || runId !== activeRunIdRef.current) {
      return false;
    }

    const responseTime = startTimeRef.current 
      ? Math.floor((Date.now() - startTimeRef.current) / 1000)
      : 0;
    
    setFinal(finalContent);
    statusRef.current = "finished";
    setStatus("finished");
    setBadgeVisible(true);
    setLastResultType('success');
    activeRunIdRef.current = null;
    abortRef.current = null;
    
    // Update the AI message with final content
    setConversation(prev => prev.map(msg =>
      msg.id === messageId
        ? {
            ...msg,
            content: finalContent,
            isThinking: false,
            isStreaming: false,
            responseTime: responseTime
          }
        : msg
    ));
    
    console.log("Task finished in", responseTime, "seconds");
    return true;
  }, []);
  
  /**
   * Set error state
   * Called by BackgroundRunner if request fails
   */
  const setError = useCallback((errorMessage, runId = activeRunIdRef.current, messageId = currentMessageIdRef.current) => {
    if (!runId || runId !== activeRunIdRef.current) {
      return false;
    }

    statusRef.current = "error";
    setStatus("error");
    setFinal(errorMessage);
    activeRunIdRef.current = null;
    
    // Update AI message to show error
    setConversation(prev => prev.map(msg =>
      msg.id === messageId
        ? {
            ...msg,
            content: `Error: ${errorMessage}`,
            type: "error",
            isThinking: false,
            isStreaming: false
          }
        : msg
    ));
    
    // Clean up abort controller
    if (abortRef.current) {
      abortRef.current = null;
    }
    
    console.error("Task error:", errorMessage);
    return true;
  }, []);
  
  /**
   * Clear the notification badge
   * Called when user views the AI page
   */
  const clearBadge = useCallback(() => {
    setBadgeVisible(false);
  }, []);
  
  /**
   * Abort the current task
   * Called when user clicks "Stop" button
   */
  const abort = useCallback(() => {
    const controller = abortRef.current;
    const messageId = currentMessageIdRef.current;

    if (!controller && statusRef.current !== "running") {
      return;
    }

    console.log("X Aborting task...");
    if (controller && !controller.signal.aborted) {
      controller.abort();
    }

    abortRef.current = null;
    activeRunIdRef.current = null;
    statusRef.current = "idle";
    setStatus("idle");

    // Update message to show it was interrupted
    setConversation(prev => prev.map(msg =>
      msg.id === messageId
        ? {
            ...msg,
            content: `${msg.content || ""}\n\n_[Response was interrupted]_`.trim(),
            isThinking: false,
            isStreaming: false
          }
        : msg
    ));
  }, []);
  
  /**
   * Clear entire conversation
   * Called when user clicks "Clear" button
   */
  const clearConversation = useCallback(() => {
    if (abortRef.current && !abortRef.current.signal.aborted) {
      abortRef.current.abort();
    }
    abortRef.current = null;
    activeRunIdRef.current = null;
    statusRef.current = "idle";

    setConversation([]);
    setStatus("idle");
    setPrompt("");
    setPartial("");
    setFinal("");
    setMessageMetadata({});
    
    // Also clear from localStorage
    localStorage.removeItem('ai_conversation_backup');
    console.log("🗑️ Conversation cleared");
    }, []);
  
  /**
   * Add metadata to a message (thinking process, follow-ups, etc.)
   */
  const addMessageMetadata = useCallback((messageId, metadata) => {
    setMessageMetadata(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        ...metadata
      }
    }));
  }, []);

  /**
 * Update a specific message in conversation
 * Used for adding follow-ups, updating content, etc.
 */
const updateMessage = useCallback((messageId, updates) => {
  setConversation(prev => prev.map(msg =>
    msg.id === messageId
      ? { ...msg, ...updates }
      : msg
  ));
}, []);
  
  // ============================================
  // CONTEXT VALUE
  // This is what components get when they call useAI()
  // ============================================
  const contextValue = {
    // State (READ)
    status,
    conversation,
    prompt,
    model,
    partial,
    final,
    badgeVisible,
    lastResultType,
    messageMetadata,
    activeModel,
    
    // Actions (WRITE)
    startTask,
    updatePartial,
    setFinished,
    setError,
    clearBadge,
    abort,
    clearConversation,
    addMessageMetadata,
    updateMessage,
    setActiveModel,

    // Refs (for BackgroundRunner)
    abortRef,
    activeRunIdRef,
    startTimeRef,
    currentMessageIdRef
  };
  
  return (
    <AIContext.Provider value={contextValue}>
      {children}
    </AIContext.Provider>
  );
}
